/**
 * MOVED OUT OF `app/records/table/` WITH THE WORKFLOW BUILDER (workflow-builder.md D37).
 *
 * The condition editor of a workflow IS this component - D14 leaves no other legal option
 * (INV-6: one AST, two backends, no third evaluator, and therefore no second editor) - and
 * importing it from `app/workflows/` into `app/records/table/` is the kind of cross-module
 * edge that becomes a cycle. A file move, not a rewrite: `FilterMenu` and
 * `TableSheetControls` import it from here and are otherwise untouched.
 */
import type { FilterOperator } from '@ofmos/shared/field-types';
import { type ReactNode, useId, useMemo, useState } from 'react';
import type { FieldMetadata, ViewFilterNode } from '../../data/client';
import { selectOptionsOf } from '../../fields/fieldTypeMeta';
import { useRelationLabel } from '../../fields/useRelationLabel';
import {
  Button,
  CellEditor,
  CONTROL_TRIGGER,
  cx,
  Icon,
  IconButton,
  Input,
  OptionList,
  SelectMenu,
} from '../../ui';
import {
  anchorChoices,
  choiceFor,
  describeViewFilter,
  EMPTY_FILTER_MODEL,
  type FilterCondition,
  type FilterModel,
  type FilterRelatedRow,
  filterInputKind,
  fromViewFilter,
  isFilterableField,
  newCondition,
  newRelatedRow,
  OPERATOR_LABELS,
  operatorsFor,
  phraseTakesNumber,
  RELATION_PHRASES,
  RELATIVE_DATE_LABELS,
  RELATIVE_DATE_TOKENS,
  type RelationContext,
  type RelationPhrase,
  relatedRowHint,
  relatedRowIssue,
  relationNeedsOfModel,
  relationNeedsOfNode,
  toViewFilter,
} from './filterModel';
import { RelationOperandInput } from './RelationOperandInput';
import { useRelationContext } from './useRelationContext';

/**
 * THE FILTER EDITOR, once, in two layouts (2026-07-30).
 *
 * It used to live inside `FilterMenu`, which meant the phone got the desktop popover:
 * a 300px-wide panel floating over the Adjust sheet, with its "Any of these" option set
 * squeezed into a column narrower than the labels in it (Arif, on his own phone). The
 * shape had to change on a phone; the SEMANTICS must not, because a filter is a stored
 * document that a saved view, an API client and Oliver all write - two editors would be
 * two answers to "what does Apply do to a filter this editor cannot represent".
 *
 * So this is the builder, and the two callers place it: `ListHeaderRow` in the desktop
 * popover (`popover`), the Adjust sheet's Filter sub-page as block content (`block`).
 * The only differences between them are width, padding and how an option set is laid
 * out - never what a control does.
 *
 * THE DRAFT IS SEEDED ON MOUNT and dropped when this component goes away. Both callers
 * mount it only while it is on screen (the popover opens, the sub-page is pushed), so
 * this is the same rule the popover always had, expressed as a lifetime instead of as an
 * open handler: a half-typed condition survives a background refetch, and reopening on a
 * view somebody else switched to shows THAT view's filter rather than the last one's
 * leftovers.
 *
 * A FILTER THIS EDITOR CANNOT REPRESENT IS SHOWN, NOT REWRITTEN. A nested tree, a negated
 * group or an operand shape the flat model has no control for renders as a read-only
 * sentence with Clear beside it. Applying over one would destroy something a person, an
 * API client or Oliver deliberately saved.
 */
export type FilterBuilderLayout = 'popover' | 'block';

export interface FilterBuilderProps {
  /** The object being filtered, so D27's anchor set can be read for it (D21, D27). */
  readonly objectId: string;
  readonly fields: readonly FieldMetadata[];
  /** The applied filter, in its STORED form (relative-date tokens intact). */
  readonly value: ViewFilterNode | null;
  readonly onChange: (filter: ViewFilterNode | null) => void;
  /** Applying or clearing LEAVES: the popover closes, the sub-page goes back. */
  readonly onDone: () => void;
  readonly layout?: FilterBuilderLayout | undefined;
  /**
   * D25: A WORKFLOW CONDITION AND A WEBHOOK FILTER CANNOT ASK ABOUT RELATED RECORDS, and
   * the server refuses one at SAVE TIME. This is that refusal rendered as an absence: the
   * one surface that shares this editor and is not a record list does not offer the row,
   * so nobody builds a filter that is then rejected. `false` also stops D27's read from
   * being issued at all on a screen that could never use it.
   */
  readonly allowRelated?: boolean | undefined;
}

export function FilterBuilder({
  objectId,
  fields,
  value,
  onChange,
  onDone,
  layout = 'popover',
  allowRelated = true,
}: FilterBuilderProps) {
  const block = layout === 'block';
  const candidates = fields.filter(isFilterableField);
  const idPrefix = useId();
  const [nextId, setNextId] = useState(0);
  const [model, setModel] = useState<FilterModel | null>(null);

  /**
   * THE CONTEXT IS READ FOR THE APPLIED FILTER **AND** THE DRAFT, because those are two
   * different sets: a row the user just added names an object the stored filter never did,
   * and the picker cannot offer its fields until they arrive.
   */
  const needs = useMemo(
    () => [...relationNeedsOfNode(value), ...(model === null ? [] : relationNeedsOfModel(model))],
    [value, model],
  );
  const relations = useRelationContext(allowRelated ? objectId : '', needs);
  const anchors = anchorChoices(relations.anchorsOf(objectId) ?? []);

  const representable = fromViewFilter(value, fields, relations);
  /**
   * THE DRAFT IS SEEDED ON MOUNT and once more when the anchor set lands, and the second
   * half is not a workaround for the first: `fromViewFilter` cannot build a related ROW
   * before it can resolve the anchor (D27's read is what says which object a hop reaches),
   * so a related view mounted before the read would seed an EMPTY draft and Apply would
   * write the filter away - the exact failure C2 named, arriving through the loading state
   * instead of through the walker. Seeding stops the moment there is a draft to protect.
   */
  const live = model ?? representable ?? EMPTY_FILTER_MODEL;

  const patch = (next: FilterModel) => setModel(next);
  const patchRow = (id: string, next: FilterCondition | FilterRelatedRow) =>
    patch({ ...live, rows: live.rows.map((row) => (row.id === id ? next : row)) });

  const add = () => {
    const field = candidates[0];
    if (field === undefined) return;
    setNextId((current) => current + 1);
    patch({ ...live, rows: [...live.rows, newCondition(field, `${idPrefix}-${nextId}`)] });
  };

  const addRelated = () => {
    const first = anchors[0];
    if (first === undefined) return;
    setNextId((current) => current + 1);
    const row = newRelatedRow(first.anchor, `${idPrefix}-${nextId}`);
    patch({ ...live, rows: [...live.rows, { ...row, anchorKey: first.key }] });
  };

  const apply = () => {
    onDone();
    onChange(toViewFilter(live, fields, relations));
  };

  const clear = () => {
    onDone();
    onChange(null);
  };

  return (
    /* NEITHER LAYOUT OWNS ITS BOX (owner click-through, wave 2b). The popover arm used to
       add `w-80 p-3` INSIDE `ui/Popover`, which is already 20rem wide and already padded:
       the panel's content box is 296px, the builder forced 320px into it, and the result
       was 24px of padding on the left, none on the right, and a card and an Apply button
       hanging over the panel's own border. The panel pads its content and the sheet pads
       its sub-page - which is how `GroupMenu` has always used the same popover. */
    <div className="space-y-3">
      {value !== null && representable === null ? (
        /**
         * The read-only branch. It states what it is and offers the two things that are
         * safe: leave it alone, or clear it and start again. "Save as new view" lives in
         * the view menu beside this control, which is where saving belongs.
         */
        <div className="space-y-2">
          <p className="text-sm text-ink">{describeViewFilter(value, fields)}</p>
          <p className="text-sm text-ink-muted">
            This filter is more detailed than this editor can show, so it is kept exactly as it was
            saved. Clear it to build a new one.
          </p>
        </div>
      ) : (
        <>
          {live.rows.length > 1 && (
            <div className={cx('flex items-center gap-2', block && 'justify-between')}>
              <span className="text-xs text-ink-muted">Match</span>
              <div className={block ? 'w-44' : 'w-32'}>
                <SelectMenu
                  value={live.combinator}
                  onValueChange={(combinator) =>
                    patch({ ...live, combinator: combinator === 'or' ? 'or' : 'and' })
                  }
                  options={[
                    { value: 'and', label: 'all of these' },
                    { value: 'or', label: 'any of these' },
                  ]}
                  ariaLabel="Match all or any"
                />
              </div>
            </div>
          )}

          {live.rows.map((row) => {
            const remove = () =>
              patch({ ...live, rows: live.rows.filter((entry) => entry.id !== row.id) });
            return row.kind === 'related' ? (
              <RelatedRow
                key={row.id}
                row={row}
                onObjectId={objectId}
                relations={relations}
                block={block}
                onChange={(next) => patchRow(row.id, next)}
                onRemove={remove}
              />
            ) : (
              <ConditionRow
                key={row.id}
                condition={row}
                candidates={candidates}
                block={block}
                onChange={(next) => patchRow(row.id, next)}
                onRemove={remove}
              />
            );
          })}

          {/*
            A COLUMN IN BOTH LAYOUTS (screenshot round, wave 2). Side by side in the popover
            both labels wrapped onto two lines and the pair read as one cramped control; a
            list of two actions is what the sheet already renders and what fits.

            AND EACH ONE IS A ROW, NOT A CENTRED LABEL (owner click-through, wave 2b). A
            column stretches its children, so a button that only asked for its content width
            became full width anyway and centred its own icon and label - two plus signs
            floating in the middle of a panel whose every other row starts at the left edge.
            `block` states the width the layout was giving them regardless, and
            `justify-start` puts the icon where the reader's eye already is. It is the shape
            the sidebar's Search button has carried since WP6b.
          */}
          <div className="flex flex-col gap-1">
            <Button
              variant="ghost"
              size={block ? 'md' : 'sm'}
              icon="plus"
              block
              className="justify-start"
              onClick={add}
            >
              Add condition
            </Button>
            {/*
              A SEPARATE AFFORDANCE, and `isFilterableField` does NOT widen to make room for
              it (D21). The scalar picker is still the scalar picker: a MANY_TO_MANY owns no
              column and a plain condition on one is still refused by the planner, and a
              reverse ONE_TO_MANY is still invisible to the whole web app. What this button
              adds is a different KIND of row, offered only where D27's read found something
              to anchor on.
            */}
            {anchors.length > 0 && (
              <Button
                variant="ghost"
                size={block ? 'md' : 'sm'}
                icon="plus"
                block
                className="justify-start"
                onClick={addRelated}
              >
                Add related filter
              </Button>
            )}
          </div>
        </>
      )}

      <div className="flex items-center justify-between gap-2 pt-1">
        <Button
          variant="ghost"
          size={block ? 'md' : 'sm'}
          onClick={clear}
          disabled={value === null}
        >
          Clear
        </Button>
        {representable !== null && (
          <Button variant="primary" size={block ? 'md' : 'sm'} onClick={apply}>
            Apply
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * D21's RELATED ROW: which relation, how many, and the ONE condition the related record
 * must satisfy.
 *
 * ```
 * [ Deals v ] [ has more than v ] [ 3 ]
 * [ Stage v ] [ is v ] [ Open ]
 * ```
 *
 * FOUR THINGS ARE DECIDED HERE RATHER THAN LEFT TO A LATER SCREEN:
 *
 *  - THE ANCHOR SELECT IS D27's PUBLISHED SET, verbatim, never a union the browser
 *    computes - which is what M3 found could not reach a hidden junction at all, and a
 *    hidden junction is the only handle an N:N filter has;
 *  - AN EMPTY FIELD SELECT IS EXISTENCE, not an unfinished row. "Agencies with at least one
 *    deal" has no inner predicate to carry, and `children: []` compiles to a bare EXISTS
 *    and never to `true` (D1's asymmetry). So the placeholder says "any record" rather than
 *    "Choose a field";
 *  - EXACTLY ONE INNER CONDITION (D21). Two is a nested tree in a flat editor;
 *  - THE SECOND HOP IS A ROW OF THE SAME SHAPE, one level deep and no further. That is what
 *    makes the far side of an N:N askable at all: anchoring on a junction's endpoint puts
 *    the subquery over the JUNCTION, so the membership's own fields are hop one and the
 *    person is hop two (D2, D7).
 */
function RelatedRow({
  row,
  onObjectId,
  relations,
  block,
  depth = 0,
  onChange,
  onRemove,
}: {
  readonly row: FilterRelatedRow;
  readonly onObjectId: string;
  readonly relations: RelationContext;
  readonly block: boolean;
  readonly depth?: number;
  readonly onChange: (next: FilterRelatedRow) => void;
  readonly onRemove: () => void;
}) {
  /**
   * THE SECOND HOP'S CONTROLS NAME THEMSELVES. Two rows of one shape on one screen give a
   * screen reader - and a test - two "Related records" comboboxes, which is a coin toss.
   */
  const nth = depth === 0 ? '' : ' (second relation)';
  const choices = anchorChoices(relations.anchorsOf(onObjectId) ?? []);
  const resolved = choiceFor(relations, onObjectId, row.anchorKey);
  const relatedObjectId = resolved?.anchor.relatedObjectId;
  const relatedFields = (
    relatedObjectId === undefined ? undefined : relations.fieldsOf(relatedObjectId)
  )?.filter(isFilterableField);
  const subject = resolved?.anchor.label ?? 'related records';
  const nestedChoices =
    relatedObjectId === undefined ? [] : (relations.anchorsOf(relatedObjectId) ?? []);

  const innerField =
    row.inner === null
      ? undefined
      : relatedFields?.find((field) => field.id === row.inner?.fieldId);
  const issue = relatedRowIssue(row, subject);
  const hint = issue === null ? relatedRowHint(row) : null;
  const inner = row.inner;

  const anchorSelect = (
    <SelectMenu
      value={row.anchorKey}
      onValueChange={(anchorKey) =>
        // Changing the relation RESETS the hop: an inner condition names a field of
        // the object we are leaving, which is a value the validator refuses.
        onChange({ ...row, anchorKey, inner: null, nested: null })
      }
      options={choices.map((choice) => ({ value: choice.key, label: choice.label }))}
      ariaLabel={`Related records${nth}`}
      placeholder="Related records"
    />
  );

  /*
    STACKED, and the desktop is the one that had to change (screenshot round, wave 2).
    Side by side inside the 320px popover the number input took the row and squeezed the
    select to a stub: "has more than" wrapped onto three lines and the chevron was clipped.
    There is no width at which a seven-phrase select and a number share 280px comfortably.
  */
  const phraseControls = (
    <div className="space-y-2">
      <SelectMenu
        value={row.phrase}
        onValueChange={(phrase) => onChange({ ...row, phrase: phrase as RelationPhrase })}
        options={RELATION_PHRASES.map((entry) => ({ value: entry.value, label: entry.label }))}
        ariaLabel={`How many related records${nth}`}
      />
      {phraseTakesNumber(row.phrase) && (
        <Input
          aria-label={`How many${nth}`}
          type="number"
          min={0}
          step={1}
          value={row.count}
          onChange={(event) => onChange({ ...row, count: event.target.value })}
        />
      )}
    </div>
  );

  const innerFieldSelect = (
    <SelectMenu
      value={inner === null ? undefined : inner.fieldId}
      onValueChange={(fieldId) => {
        const field = relatedFields?.find((candidate) => candidate.id === fieldId);
        if (field === undefined) return;
        onChange({ ...row, inner: newCondition(field, `${row.id}-i`), nested: null });
      }}
      options={(relatedFields ?? []).map((field) => ({
        value: field.id,
        label: field.label,
      }))}
      ariaLabel={`Field on the related records${nth}`}
      placeholder="any record"
    />
  );

  /*
    THE SECOND HOP, OFFERED ONLY AT THE FIRST LEVEL AND ONLY WHERE THERE IS ONE.
    D7 caps a filter at two hops and enforces it at four entrances; the editor's
    share of that cap is structural - a nested row renders no button of its own, so
    a third hop is not something a person can build and then be refused for.
  */
  const addSecondHop = depth === 0 && inner === null && nestedChoices.length > 0 && (
    <Button
      variant="ghost"
      size={block ? 'md' : 'sm'}
      icon="plus"
      block
      className="justify-start"
      onClick={() => {
        const first = anchorChoices(nestedChoices)[0];
        if (first === undefined) return;
        onChange({
          ...row,
          inner: null,
          nested: {
            ...newRelatedRow(first.anchor, `${row.id}-n`),
            anchorKey: first.key,
          },
        });
      }}
    >
      Add relation
    </Button>
  );

  const remove = (
    <IconButton
      icon="close"
      size={block ? 'md' : 'sm'}
      label={`Remove the ${subject} filter`}
      onClick={onRemove}
    />
  );

  const nestedRow = row.nested !== null && relatedObjectId !== undefined && (
    <>
      {/*
        THE CONNECTOR (screenshot round, wave 2). Without it the second hop is an inset
        card that reads as a SIBLING of the first: "W2 deals / has any" then a card
        saying "W2 person / has any / City". The two are one sentence and the sentence
        needs its conjunction.
      */}
      <p className="text-xs text-ink-muted">where that record:</p>
      <RelatedRow
        row={row.nested}
        onObjectId={relatedObjectId}
        relations={relations}
        block={block}
        depth={depth + 1}
        onChange={(next) => onChange({ ...row, nested: next })}
        onRemove={() => onChange({ ...row, nested: null })}
      />
    </>
  );

  const sentences = (
    <>
      {issue !== null && <p className="text-xs text-danger">{issue}</p>}
      {hint !== null && <p className="text-xs text-ink-muted">{hint}</p>}
    </>
  );

  /**
   * ON A DESKTOP THE HOP IS A CHIP (`ui-foundation.md` D11). The row is the same one line
   * a scalar condition is - value cell and all - and everything that describes the HOP
   * rather than the condition (which relation, how many, and the second hop) lives in a
   * nested layer behind a chip that reads "Deals has more than 3". Six controls on one
   * line would be a row nobody can read; the hop is one idea and it gets one control.
   */
  if (!block) {
    return (
      <div className="space-y-2">
        <div className="flex min-h-10 items-center gap-2">
          <div className="min-w-0 flex-[4]">
            <ValueLayer
              label={`Related records${nth}`}
              summary={hopSummary(subject, row)}
              empty={false}
            >
              <div className="space-y-2">
                {anchorSelect}
                {phraseControls}
                {addSecondHop}
              </div>
            </ValueLayer>
          </div>
          <div className="min-w-0 flex-[3]">{innerFieldSelect}</div>
          {inner !== null && innerField !== undefined ? (
            <>
              <div className="min-w-0 flex-[3]">
                <OperatorSelect
                  condition={inner}
                  field={innerField}
                  labelSuffix={nth}
                  onChange={(next) => onChange({ ...row, inner: next })}
                />
              </div>
              <div className="min-w-0 flex-[4]">
                <OperandControl
                  condition={inner}
                  field={innerField}
                  block={false}
                  labelSuffix={nth}
                  onChange={(next) => onChange({ ...row, inner: next })}
                />
              </div>
            </>
          ) : (
            /* The EXISTENCE case: no field picked is a complete question, so the row ends
               here rather than showing two empty controls nobody has to fill in. */
            <div className="min-w-0 flex-[7]" />
          )}
          {remove}
        </div>
        {sentences}
        {nestedRow}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-surface border border-line-subtle p-3">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">{anchorSelect}</div>
        {remove}
      </div>

      {phraseControls}
      {sentences}

      {row.nested !== null && relatedObjectId !== undefined ? (
        nestedRow
      ) : (
        <>
          {/*
            ONE FIELD PICKER, NOT TWO. The inner condition is part of THIS row rather than a
            condition row nested inside it, so it borrows the operator and operand controls
            and brings its own field select - the one whose empty state is the existence
            case. A second remove button beside a second field select would be a row that
            looks like it can hold more than one.
          */}
          {innerFieldSelect}
          {inner !== null && innerField !== undefined && (
            <OperatorAndOperand
              condition={inner}
              field={innerField}
              labelSuffix={nth}
              onChange={(next) => onChange({ ...row, inner: next })}
            />
          )}
          {addSecondHop}
        </>
      )}
    </div>
  );
}

/** "Deals has more than 3", which is the whole hop in the words the phrase select uses. */
function hopSummary(subject: string, row: FilterRelatedRow): string {
  const phrase = RELATION_PHRASES.find((entry) => entry.value === row.phrase)?.label ?? '';
  const count = phraseTakesNumber(row.phrase) ? ` ${row.count}` : '';
  return `${subject} ${phrase}${count}`.trim();
}

/**
 * ONE CONDITION, IN TWO SHAPES (`ui-foundation.md` D11).
 *
 * ON A DESKTOP IT IS ONE 40px LINE - field, operator, value, remove - the shape of a
 * Finder search criteria row, and the reason is the popover: a card per condition made the
 * panel taller with every condition somebody added, and five of them ran off the screen.
 * A row is also how the thing READS: "Status is one of Onboarding, Active" is a sentence
 * left to right, not a stack.
 *
 * IN THE SHEET IT STAYS A CARD, because that layout is right for a sheet: full width, one
 * control under the next, nothing squeezed into 280px.
 */
function ConditionRow({
  condition,
  candidates,
  block,
  onChange,
  onRemove,
}: {
  readonly condition: FilterCondition;
  readonly candidates: readonly FieldMetadata[];
  readonly block: boolean;
  readonly onChange: (next: FilterCondition) => void;
  readonly onRemove: () => void;
}) {
  const field = candidates.find((candidate) => candidate.id === condition.fieldId);
  if (field === undefined) return null;

  const fieldSelect = (
    <SelectMenu
      value={condition.fieldId}
      onValueChange={(fieldId) => {
        const next = candidates.find((candidate) => candidate.id === fieldId);
        if (next === undefined) return;
        // Changing the field RESETS the operand: an option id from another field's
        // vocabulary is a value the normalizer refuses, not a value to carry over.
        onChange({ ...newCondition(next, condition.id) });
      }}
      options={candidates.map((candidate) => ({
        value: candidate.id,
        label: candidate.label,
      }))}
      ariaLabel="Filter field"
    />
  );
  const remove = (
    <IconButton
      icon="close"
      size={block ? 'md' : 'sm'}
      label={`Remove the ${field.label} condition`}
      onClick={onRemove}
    />
  );

  if (!block) {
    return (
      <div className="flex min-h-10 items-center gap-2">
        <div className="min-w-0 flex-[4]">{fieldSelect}</div>
        <div className="min-w-0 flex-[4]">
          <OperatorSelect condition={condition} field={field} onChange={onChange} />
        </div>
        <div className="min-w-0 flex-[5]">
          <OperandControl condition={condition} field={field} block={false} onChange={onChange} />
        </div>
        {remove}
      </div>
    );
  }

  return (
    /* A CARD, full width in the sheet. */
    <div className="space-y-3 rounded-surface border border-line-subtle p-3">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">{fieldSelect}</div>
        {remove}
      </div>

      <OperatorAndOperand condition={condition} field={field} onChange={onChange} />
    </div>
  );
}

/**
 * EVERYTHING A CONDITION IS EXCEPT WHICH FIELD IT IS ABOUT, STACKED - which is the SHEET's
 * shape (D21, and `ui-foundation.md` D11 for the split).
 *
 * Extracted for the related row (D21), which owns its own field select - the one whose
 * EMPTY state is the existence case - and needs the same operator and operand controls
 * under it. Two copies would be two answers to "what does `between` look like".
 *
 * The desktop row does not use it: there the two halves sit in two cells of one line, so
 * they are two components and this is the sheet's composition of them.
 */
function OperatorAndOperand({
  condition,
  field,
  labelSuffix = '',
  onChange,
}: {
  readonly condition: FilterCondition;
  readonly field: FieldMetadata;
  readonly labelSuffix?: string;
  readonly onChange: (next: FilterCondition) => void;
}) {
  return (
    <>
      <OperatorSelect
        condition={condition}
        field={field}
        labelSuffix={labelSuffix}
        onChange={onChange}
      />
      <OperandControl
        condition={condition}
        field={field}
        block
        labelSuffix={labelSuffix}
        onChange={onChange}
      />
    </>
  );
}

/** Which comparison. The same control in both shapes, and the same vocabulary (INV-12). */
function OperatorSelect({
  condition,
  field,
  labelSuffix = '',
  onChange,
}: {
  readonly condition: FilterCondition;
  readonly field: FieldMetadata;
  readonly labelSuffix?: string;
  readonly onChange: (next: FilterCondition) => void;
}) {
  return (
    <SelectMenu
      value={condition.operator}
      onValueChange={(operator) =>
        onChange({
          ...condition,
          operator: operator as FilterOperator,
          value: '',
          values: [],
          relative: null,
        })
      }
      options={operatorsFor(field).map((operator) => ({
        value: operator,
        label: OPERATOR_LABELS[operator],
      }))}
      ariaLabel={`Filter operator${labelSuffix}`}
    />
  );
}

/**
 * THE VALUE, AND WHERE IT IS EDITED (`ui-foundation.md` D11).
 *
 * In the SHEET every operand is a control in the stack, exactly as it always was. On a
 * DESKTOP the condition is one 40px line, so an operand that cannot BE one control opens
 * its editor as a NESTED LAYER anchored to the row's value cell - the option list, the
 * record picker, the two ends of a date range - and the cell shows what is in it: "
 * Onboarding, Active +1". The ones that fit a cell stay in the cell: text, number, a
 * single date, yes/no, one option.
 *
 * The layer is `ui/CellEditor` with `nested`, which is the mechanism that already exists
 * for "an overlay opened from inside a non-modal one" (`ui/nestedLayer.ts`). Without the
 * flag the filter popover would dismiss the moment somebody ticked an option in it.
 */
function OperandControl({
  condition,
  field,
  block,
  labelSuffix = '',
  onChange,
}: {
  readonly condition: FilterCondition;
  readonly field: FieldMetadata;
  readonly block: boolean;
  readonly labelSuffix?: string;
  readonly onChange: (next: FilterCondition) => void;
}) {
  const kind = filterInputKind(field, condition.operator);

  if (kind === 'none') return null;

  if (kind === 'option') {
    return (
      <SelectMenu
        value={condition.value === '' ? undefined : condition.value}
        onValueChange={(value) => onChange({ ...condition, value })}
        options={selectOptionsOf(field).map((option) => ({
          value: option.id,
          label: option.label,
        }))}
        ariaLabel={`Filter value${labelSuffix}`}
        placeholder="Choose an option"
      />
    );
  }

  if (kind === 'boolean') {
    return (
      <SelectMenu
        value={condition.value === '' ? undefined : condition.value}
        onValueChange={(value) => onChange({ ...condition, value })}
        options={[
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ]}
        ariaLabel={`Filter value${labelSuffix}`}
        placeholder="Yes or no"
      />
    );
  }

  if (kind === 'optionList') {
    /**
     * D20's multi-value SELECT operand: a checkbox list over the same vocabulary the
     * single-select editor renders. Without it D22's Agencies view - `lifecycle_status
     * in (onboarding, active)` - would be a seeded default nobody could edit.
     *
     * ONE PRIMITIVE, NO LAYOUT PROP (D2 and D9). This branch used to hold two hand-built
     * lists - a row per option in the sheet, bare `Checkbox` children in the popover - and
     * the popover half was the 2026-09-05 bug: `Checkbox` is `inline-flex`, so five
     * options flowed like words and the next box painted over the tail of the previous
     * label. `OptionList` also bounds itself at six rows now (D10), which is what makes it
     * fit in a layer at all.
     */
    const list = (
      <OptionList
        legend="Any of these"
        options={selectOptionsOf(field).map((option) => ({
          value: option.id,
          label: option.label,
        }))}
        value={condition.values}
        onChange={(values) => onChange({ ...condition, values })}
      />
    );
    return block ? (
      list
    ) : (
      <ValueLayer
        label={`Filter value${labelSuffix}`}
        summary={optionSummary(field, condition.values)}
        empty={condition.values.length === 0}
      >
        {list}
      </ValueLayer>
    );
  }

  if (kind === 'dateRange') {
    /**
     * D17 in the editor. A relative token is stored INSTEAD of two dates, so the two
     * controls are exclusive - and choosing "A fixed range" is what makes a view stop
     * moving with the calendar.
     */
    const range = (
      <div className="space-y-2">
        <SelectMenu
          value={condition.relative ?? 'fixed'}
          onValueChange={(token) =>
            onChange({
              ...condition,
              relative: token === 'fixed' ? null : (token as FilterCondition['relative']),
              values: [],
            })
          }
          options={[
            { value: 'fixed', label: 'A fixed range' },
            ...RELATIVE_DATE_TOKENS.map((token) => ({
              value: token,
              label: RELATIVE_DATE_LABELS[token],
            })),
          ]}
          ariaLabel={`Date range${labelSuffix}`}
        />
        {condition.relative === null && (
          /**
           * TWO DATE INPUTS, ONE UNDER THE OTHER (measured, wave 1). Side by side they were
           * 331px of controls in the panel's 318px box, with the panel's own
           * `overflow-x-hidden` cutting the "To" input off. A date input has a minimum
           * width the browser sets, so the row cannot be made to fit; it can only stop
           * being a row.
           */
          <div className="space-y-2">
            <Input
              aria-label={`From${labelSuffix}`}
              type="date"
              value={condition.values[0] ?? ''}
              onChange={(event) =>
                onChange({
                  ...condition,
                  values: [event.target.value, condition.values[1] ?? ''],
                })
              }
            />
            <Input
              aria-label={`To${labelSuffix}`}
              type="date"
              value={condition.values[1] ?? ''}
              onChange={(event) =>
                onChange({
                  ...condition,
                  values: [condition.values[0] ?? '', event.target.value],
                })
              }
            />
          </div>
        )}
      </div>
    );
    return block ? (
      range
    ) : (
      <ValueLayer
        label={`Filter value${labelSuffix}`}
        summary={rangeSummary(condition)}
        empty={condition.relative === null && (condition.values[0] ?? '') === ''}
      >
        {range}
      </ValueLayer>
    );
  }

  if (kind === 'relation') {
    const picker = (
      <RelationOperandInput
        field={field}
        value={condition.value}
        onChange={(value) => onChange({ ...condition, value })}
      />
    );
    return block ? (
      picker
    ) : (
      <RelationValueLayer
        field={field}
        condition={condition}
        labelSuffix={labelSuffix}
        onChange={onChange}
      />
    );
  }

  return (
    <Input
      aria-label={`Filter value${labelSuffix}`}
      type={kind === 'number' ? 'number' : kind === 'date' ? 'date' : 'text'}
      value={condition.value}
      onChange={(event) => onChange({ ...condition, value: event.target.value })}
    />
  );
}

/**
 * A CELL THAT SHOWS ITS VALUE AND OPENS ITS EDITOR (D11), which is the Finder row's move:
 * the criteria line stays a line, and the thing that needs room gets a layer.
 */
function ValueLayer({
  label,
  summary,
  empty,
  children,
}: {
  readonly label: string;
  readonly summary: string;
  /** Nothing chosen yet: the summary is a prompt and reads as placeholder text. */
  readonly empty: boolean;
  readonly children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <CellEditor
      open={open}
      onOpenChange={setOpen}
      nested
      label={label}
      anchor={
        <button
          type="button"
          aria-label={label}
          // The sweep opens every one of these and measures what is inside it.
          data-filter-value-layer=""
          onClick={() => setOpen(true)}
          className={cx(CONTROL_TRIGGER, 'text-left')}
        >
          <span className={cx('min-w-0 truncate', empty && 'text-ink-subtle')}>{summary}</span>
          <Icon name="chevron-down" className="shrink-0 text-ink-subtle" />
        </button>
      }
    >
      {children}
    </CellEditor>
  );
}

/** The relation operand's cell, which has to RESOLVE the chosen record to show its name. */
function RelationValueLayer({
  field,
  condition,
  labelSuffix,
  onChange,
}: {
  readonly field: FieldMetadata;
  readonly condition: FilterCondition;
  readonly labelSuffix: string;
  readonly onChange: (next: FilterCondition) => void;
}) {
  const chosen = useRelationLabel(field, condition.value === '' ? null : condition.value);
  return (
    <ValueLayer
      label={`Filter value${labelSuffix}`}
      summary={condition.value === '' ? 'Choose a record' : (chosen.label ?? 'One record')}
      empty={condition.value === ''}
    >
      <RelationOperandInput
        field={field}
        value={condition.value}
        onChange={(value) => onChange({ ...condition, value })}
      />
    </ValueLayer>
  );
}

/** "Onboarding, Active +1": two labels and a count, which is what a cell can hold. */
function optionSummary(field: FieldMetadata, values: readonly string[]): string {
  if (values.length === 0) return 'Choose options';
  const labels = selectOptionsOf(field)
    .filter((option) => values.includes(option.id))
    .map((option) => option.label);
  const shown = labels.slice(0, 2).join(', ');
  return labels.length > 2 ? `${shown} +${labels.length - 2}` : shown;
}

/** A relative token reads as itself; a fixed range reads as its two ends. */
function rangeSummary(condition: FilterCondition): string {
  if (condition.relative !== null) return RELATIVE_DATE_LABELS[condition.relative];
  const from = condition.values[0] ?? '';
  const to = condition.values[1] ?? '';
  if (from === '' && to === '') return 'Choose a range';
  return `${from === '' ? 'any date' : from} to ${to === '' ? 'any date' : to}`;
}
